import React from 'react';
import { ButtonBase, Text, Link as ModeLink } from '@stitch-fix/mode-react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Link } from '@stitch-fix/sf-next';
import { ControlledTextField } from '../Forms';
import { SignInFormWrapper } from './SignInFormWrapper';
import { isAndroidApp } from '../../utils/registration/android';
import { loginByEmail, isLoginAuthError } from '../../utils/requests/clientAuth/login';
import { ReCaptchaV2Wrapper } from './ReCaptchaV2Wrapper';
import { SignInFormFooter } from './SignInFormFooter';
import { BannerWrapper } from './BannerWrapper';
import { SignInMachineContext } from '../../machines/sign-in/SignInMachineContext';
import { browserLogger } from '../../utils/loggers/browser';
import { emailSchema } from '../../utils/zod/emailSchema';
import type { ExecuteReCaptcha } from './ReCaptchaV3Controller';
const loginFormSchema = z.object({
  password: z.string({
    required_error: 'Please enter your password.'
  }).max(75)
}).merge(emailSchema);
type LoginFormSchema = z.infer<typeof loginFormSchema>;
interface EmailPasswordFormProps {
  executeReCaptcha?: ExecuteReCaptcha;
  reCaptchaV3Expected?: boolean;
}
export const EmailPasswordForm = ({
  executeReCaptcha,
  reCaptchaV3Expected = false
}: EmailPasswordFormProps) => {
  const [state, send] = SignInMachineContext.useActor();
  const {
    control,
    handleSubmit
  } = useForm<LoginFormSchema>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: {}
  });
  const isWafEnabled = state.context.isWafEnabled;
  const onSubmit = async (data: LoginFormSchema) => {
    let reCaptchaTokenV3: string | undefined;
    if (executeReCaptcha) {
      try {
        reCaptchaTokenV3 = await executeReCaptcha('login');
      } catch {
        browserLogger({
          level: 'warn',
          team: 'client-onboarding',
          message: 'reCAPTCHA v3 execute failed',
          feature: 'email-login'
        });
      }
    } else if (reCaptchaV3Expected) {
      // Expected a token but the executor wasn't ready — script still loading,
      // blocked, or not yet published. Log so the drop isn't silent.
      browserLogger({
        level: 'warn',
        team: 'client-onboarding',
        message: 'reCAPTCHA v3 enabled but executor not ready at submit',
        feature: 'email-login'
      });
    }
    try {
      const {
        data: {
          redirect_url
        }
      } = await loginByEmail(data.email, data.password, state.context.reCaptchaTokenV2, reCaptchaTokenV3, isWafEnabled);
      send({
        type: 'LOGIN_SUCCESS',
        redirectUrl: redirect_url
      });
    } catch (error) {
      if (isLoginAuthError(error)) {
        send({
          type: 'LOGIN_FAILURE',
          error: error.response.data,
          isWafEnabled
        });
      } else {
        let message = 'Unknown error during login';
        let stack;
        if (error instanceof Error) {
          message = error.message;
          stack = error.stack;
        }
        browserLogger({
          level: 'error',
          team: 'client-onboarding',
          message: 'Unknown error during login',
          context: {
            message,
            stack,
            isWafEnabled
          }
        });
      }
    }
  };
  return <SignInFormWrapper onSubmit={handleSubmit(onSubmit)} subheading={!isAndroidApp() && <>
            Use your password or try{' '}
            <ModeLink as={ButtonBase} onClick={() => {
      send({
        type: 'PASSWORDLESS_SIGNIN'
      });
    }}>
              password-less sign in.
            </ModeLink>
          </>} ctaText="Continue" footer={<SignInFormFooter />}>
      <BannerWrapper />
      <ControlledTextField control={control} autocomplete="username webauthn" name="email" label="Email" id="signin-email" />
      <ControlledTextField control={control} autocomplete="current-password" type="password" name="password" label="Password" id="signin-password" />
      <Text align="center" mb={2} setting="body-small">
        Forgot password? <Link href="/password/new">Reset it now</Link>
      </Text>
      {state.matches('emailPasswordSignIn.reCaptchaV2Required') && <ReCaptchaV2Wrapper key={state.context.reCaptchaV2ResetKey} onChange={token => send({
      type: 'SET_RECAPTCHA_V2_TOKEN',
      token: token ?? ''
    })} />}
    </SignInFormWrapper>;
};